import { Spin } from 'antd';
import { lazy, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AppLayout } from './components/AppLayout';
import { RequirePerm } from './components/RequirePerm';
import { LoginPage } from './pages/Login';

/** Route-level code splitting: each page (and heavy deps like Recharts) loads on first visit. */
function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => load().then((m) => ({ default: m[name] })));
}

const AuditLogsPage = page(() => import('./pages/audit/AuditLogs'), 'AuditLogsPage');
const AgenciesPage = page(() => import('./pages/business/Agencies'), 'AgenciesPage');
const ResellersPage = page(() => import('./pages/business/Resellers'), 'ResellersPage');
const AnnouncementsPage = page(() => import('./pages/content/Announcements'), 'AnnouncementsPage');
const BannersPage = page(() => import('./pages/content/Banners'), 'BannersPage');
const NewsTickerPage = page(() => import('./pages/content/NewsTicker'), 'NewsTickerPage');
const GamesPage = page(() => import('./pages/games/GamesPage'), 'GamesPage');
const PostsPage = page(() => import('./pages/content/Posts'), 'PostsPage');
const DashboardPage = page(() => import('./pages/Dashboard'), 'DashboardPage');
const FinancePage = page(() => import('./pages/economy/FinancePage'), 'FinancePage');
const GiftsPage = page(() => import('./pages/economy/GiftsPage'), 'GiftsPage');
const LevelsPage = page(() => import('./pages/economy/LevelsPage'), 'LevelsPage');
const RechargePage = page(() => import('./pages/economy/RechargePage'), 'RechargePage');
const StorePage = page(() => import('./pages/economy/StorePage'), 'StorePage');
const WithdrawalsPage = page(() => import('./pages/economy/WithdrawalsPage'), 'WithdrawalsPage');
const SettingsPage = page(() => import('./pages/SettingsPage'), 'SettingsPage');
const StoragePage = page(() => import('./pages/StoragePage'), 'StoragePage');
const HostRequestsPage = page(() => import('./pages/host-requests/HostRequests'), 'HostRequestsPage');
const LiveRoomsPage = page(() => import('./pages/live/LiveRooms'), 'LiveRoomsPage');
const CallsPage = page(() => import('./pages/live/CallsPage'), 'CallsPage');
const PkBattlesPage = page(() => import('./pages/live/PkBattles'), 'PkBattlesPage');
const ReportsPage = page(() => import('./pages/moderation/Reports'), 'ReportsPage');
const BannedUsersPage = page(() => import('./pages/moderation/BannedUsers'), 'BannedUsersPage');
const BannedDevicesPage = page(() => import('./pages/moderation/BannedDevices'), 'BannedDevicesPage');
const RolesPage = page(() => import('./pages/roles/RolesPage'), 'RolesPage');
const UserDetailPage = page(() => import('./pages/users/UserDetail'), 'UserDetailPage');
const UsersListPage = page(() => import('./pages/users/UsersList'), 'UsersListPage');

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <Spin fullscreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout />;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<Protected />}>
            <Route index element={<RequirePerm perm="analytics.view"><DashboardPage /></RequirePerm>} />
            <Route path="live" element={<RequirePerm perm="live.view"><LiveRoomsPage /></RequirePerm>} />
            <Route path="pk" element={<RequirePerm perm="live.view"><PkBattlesPage /></RequirePerm>} />
            <Route path="calls" element={<RequirePerm perm="live.view"><CallsPage /></RequirePerm>} />
            <Route path="reports" element={<RequirePerm perm="report.view"><ReportsPage /></RequirePerm>} />
            <Route path="host-requests" element={<RequirePerm perm="host_request.review"><HostRequestsPage /></RequirePerm>} />
            <Route path="agencies" element={<RequirePerm perm="agency.manage"><AgenciesPage /></RequirePerm>} />
            <Route path="resellers" element={<RequirePerm perm="reseller.manage"><ResellersPage /></RequirePerm>} />
            <Route path="posts" element={<RequirePerm perm="content.moderate"><PostsPage /></RequirePerm>} />
            <Route path="announcements" element={<RequirePerm perm="settings.manage"><AnnouncementsPage /></RequirePerm>} />
            <Route path="banners" element={<RequirePerm perm="banner.manage"><BannersPage /></RequirePerm>} />
            <Route path="news-ticker" element={<RequirePerm perm="ticker.manage"><NewsTickerPage /></RequirePerm>} />
            <Route path="games" element={<RequirePerm perm="game.manage"><GamesPage /></RequirePerm>} />
            <Route path="gifts" element={<RequirePerm perm="gift.manage"><GiftsPage /></RequirePerm>} />
            <Route path="store" element={<RequirePerm perm="store.manage"><StorePage /></RequirePerm>} />
            <Route path="levels" element={<RequirePerm perm="settings.manage"><LevelsPage /></RequirePerm>} />
            <Route path="recharge" element={<RequirePerm perm="recharge.manage"><RechargePage /></RequirePerm>} />
            <Route path="withdrawals" element={<RequirePerm perm="withdraw.approve"><WithdrawalsPage /></RequirePerm>} />
            <Route path="finance" element={<RequirePerm perm="wallet.view"><FinancePage /></RequirePerm>} />
            <Route path="settings" element={<RequirePerm perm="settings.manage"><SettingsPage /></RequirePerm>} />
            <Route path="storage" element={<RequirePerm perm="settings.manage"><StoragePage /></RequirePerm>} />
            <Route path="users" element={<RequirePerm perm="user.view"><UsersListPage /></RequirePerm>} />
            <Route path="users/:id" element={<RequirePerm perm="user.view"><UserDetailPage /></RequirePerm>} />
            <Route path="moderation/banned" element={<RequirePerm perm="user.view"><BannedUsersPage /></RequirePerm>} />
            <Route path="moderation/devices" element={<RequirePerm perm="user.view"><BannedDevicesPage /></RequirePerm>} />
            <Route path="roles" element={<RequirePerm perm="role.manage"><RolesPage /></RequirePerm>} />
            <Route path="audit" element={<RequirePerm perm="audit.view"><AuditLogsPage /></RequirePerm>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
